import React, { useState, useEffect } from 'react';

// Error Handling
import ErrorBoundary from './components/shared/ErrorBoundary';

// Auth Components
import Login from './components/auth/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import usePermissions from './hooks/usePermissions';

// Admin Components  
import MaterialManagement from './components/admin/MaterialManagement';
import CategoryManagement from './components/admin/CategoryManagement';
import VendorManagement from './components/admin/VendorManagement';
import VendorLedger from './components/vendors/VendorLedger';
import AccountingSummary from './components/vendors/AccountingSummary';
import UserManagement from './components/admin/UserManagement';
import PermissionsManagement from './components/admin/PermissionsManagement';
import UploadProfiles from './components/admin/UploadProfiles';
import BillingSettings from './components/admin/BillingSettings';
import VendorImport from './components/admin/VendorImport';
import CostBreakdown from './components/admin/CostBreakdown';

// Inventory Components
import ViewInventory from './components/inventory/ViewInventory';
import AddInventory from './components/inventory/AddInventory';
import UploadInventory from './components/inventory/UploadInventory';
import InventoryPictures from './components/inventory/InventoryPictures';

// Billing Components
import IndianBilling from './components/billing/IndianBilling';
import USBilling from './components/billing/USBilling';
import JewelryReturns from './components/billing/JewelryReturns';
import BillingHistory from './components/billing/BillingHistory';

// Estimate Components
import INREstimate from './components/estimates/INREstimate';
import USDEstimate from './components/estimates/USDEstimate';
import ViewEstimates from './components/estimates/ViewEstimates';
import EstimateCart from './components/estimates/EstimateCart';

// Reports Components
import AvailableStock from './components/reports/AvailableStock';
import SoldItems from './components/reports/SoldItems';
import VendorStock from './components/reports/VendorStock';
import CustomerList from './components/reports/CustomerList';
import SalesReport from './components/reports/SalesReport';
import ItemAging from './components/reports/ItemAging';

// Utilities Components
import RateManager from './components/utilities/RateManager';
import ExhibitionReconciliation from './components/utilities/ExhibitionReconciliation';
import BarcodeGenerator from './components/utilities/BarcodeGenerator';
import MissingImages from './components/utilities/MissingImages';
import InventoryUpload from './components/utilities/InventoryUpload';
import CatalogSettings from './components/admin/CatalogSettings';

// Gold Monthly Installment Scheme Components
import SchemeManagement from './components/schemes/SchemeManagement';
import SchemeEnrollment from './components/schemes/SchemeEnrollment';
import SchemePaymentEntry from './components/schemes/SchemePaymentEntry';
import SchemeDashboard from './components/schemes/SchemeDashboard';
import SchemeRedemption from './components/schemes/SchemeRedemption';
import SchemeReports from './components/schemes/SchemeReports';

// Shared Components
import Sidebar from './components/shared/Sidebar';
import { Dashboard } from './components/shared/Dashboard';
import ContextualHelp from './components/shared/ContextualHelp';

// Icons
import { Menu, LogOut, User } from 'lucide-react'; // eslint-disable-line no-unused-vars
function AppContent() {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const { canAccessPage } = usePermissions();
  const [currentModule, setCurrentModule] = useState('dashboard');

  useEffect(() => {
    if (isAuthenticated) {
      setCurrentModule('dashboard');
    }
  }, [isAuthenticated]);

  // Jewelry categories state for CategoryManagement
  const [jewelryCategories, setJewelryCategories] = useState([]);

  // Listen for navigation events from Dashboard quick actions
  useEffect(() => {
    const handleNavigate = (event) => {
      const module = event.detail;
      console.log('Navigation event received:', module);
      setCurrentModule(module);
    };

    window.addEventListener('navigate', handleNavigate);

    return () => {
      window.removeEventListener('navigate', handleNavigate);
    };
  }, []);

  const handleMenuClick = (moduleId) => {
    setCurrentModule(moduleId);
  };

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // Permission-based access control
  const hasAccess = (pageId) => {
    if (!user) return false;
    return canAccessPage(pageId);
  };

  const renderContent = () => {
    // Check if user has access to the current module
    if (!hasAccess(currentModule)) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-red-800 mb-2">Access Denied</h3>
            <p className="text-red-600">You don't have permission to access this feature.</p>
            <p className="text-sm text-red-500 mt-2">Current role: <span className="font-semibold">{user?.role?.replace('_', ' ').toUpperCase()}</span></p>
          </div>
        </div>
      );
    }

    switch (currentModule) {
      case 'dashboard':
        return <Dashboard />;
      case 'material-management':
        return <MaterialManagement />;
      case 'category-management':
        return <CategoryManagement
          jewelryCategories={jewelryCategories}
          setJewelryCategories={setJewelryCategories}
        />;
      case 'vendor-management':
        return <VendorManagement />;
      case 'vendor-ledger':
        return <VendorLedger />;
      case 'accounting-summary':
        return <AccountingSummary />;
      case 'user-management':
        return <UserManagement />;
      case 'permissions-management':
        return <PermissionsManagement />;
      case 'billing-settings':
        return <BillingSettings />;
      case 'vendor-import':
        return <VendorImport />;
      case 'cost-breakdown':
        return <CostBreakdown />;
      case 'upload-profiles':
        return <UploadProfiles />;
      case 'view-inventory':
        return <ViewInventory />;
      case 'add-inventory':
        return <AddInventory />;
      case 'inventory-pictures':
        return <InventoryPictures />;
      case 'upload-inventory':
        return <UploadInventory />;
      case 'indian-billing':
        return <IndianBilling />;
      case 'us-billing':
        return <USBilling />;
      case 'jewelry-returns':
        return <JewelryReturns />;
      case 'billing-history':
        return <BillingHistory />;
      case 'inr-estimate':
        return <INREstimate />;
      case 'usd-estimate':
        return <USDEstimate />;
      case 'view-estimates':
        return <ViewEstimates />;
      case 'estimate-cart':
        return <EstimateCart />;
      case 'available-stock':
        return <AvailableStock />;
      case 'sold-items':
        return <SoldItems />;
      case 'item-aging':
        return <ItemAging />;
      case 'vendor-stock':
        return <VendorStock />;
      case 'customer-list':
        return <CustomerList />;
      case 'sales-report':
        return <SalesReport />;
      case 'gold-rate':
      case 'dollar-rate':
      case 'manual-rate-entry':
        return <RateManager />;
      case 'exhibition-reconciliation':
        return <ExhibitionReconciliation />;
      case 'barcode-generator':
        return <BarcodeGenerator />;
      case 'missing-images':
        return <MissingImages />;
      case 'inventory-upload':
        return <InventoryUpload />;
      case 'catalog-settings':
        return <CatalogSettings />;
      case 'scheme-management':
        return <SchemeManagement />;
      case 'scheme-enroll':
        return <SchemeEnrollment />;
      case 'scheme-payment':
        return <SchemePaymentEntry />;
      case 'scheme-dashboard':
        return <SchemeDashboard />;
      case 'scheme-redemption':
        return <SchemeRedemption />;
      case 'scheme-reports':
        return <SchemeReports />;
      // case 'upload-jewelry':
      //   return <UploadJewelry />; // Future Enhancement
      default:
        return <Dashboard />;
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <Sidebar
        currentModule={currentModule}
        handleMenuClick={handleMenuClick}
      />

      {/* Main Content */}
      <div className="ml-16 min-w-0">
        <main className="pb-8">
          {renderContent()}
        </main>
      </div>
      {hasAccess(currentModule) && <ContextualHelp currentModule={currentModule} />}
    </div>
  );
}

// Main App with Auth Provider and Error Boundary
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
